import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  ButtonBase,
  CircularProgress,
  Collapse,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";

import { BRAND_BLUE, BRAND_GREEN } from "../../theme/colors";
import {
  fetchNotifications,
  markNotificationRead,
} from "./notificationService";

const POLL_MS = 60000;

const severityMap = {
  info: "info",
  success: "success",
  warning: "warning",
  error: "error",
};

export default function NotificationBanner({ onNavigate }) {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [closingIds, setClosingIds] = useState([]);

  const loadBanners = useCallback(async () => {
    try {
      const items = await fetchNotifications({ limit: 25 });
      setBanners(items.filter((item) => item.is_banner && !item.is_read));
    } catch {
      // Keep the app usable even if banner fetch fails.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBanners();
    const intervalId = window.setInterval(loadBanners, POLL_MS);
    return () => window.clearInterval(intervalId);
  }, [loadBanners]);

  const visibleBanners = useMemo(
    () => banners.filter((item) => !closingIds.includes(item.id)),
    [banners, closingIds],
  );

  const handleClose = useCallback(async (bannerId) => {
    setClosingIds((prev) => [...prev, bannerId]);
    try {
      await markNotificationRead(bannerId);
      setBanners((prev) => prev.filter((item) => item.id !== bannerId));
    } catch {
      setClosingIds((prev) => prev.filter((id) => id !== bannerId));
    }
  }, []);

  const handleOpenNotification = useCallback(
    (banner) => {
      window.dispatchEvent(
        new CustomEvent("be:open-notification-center", {
          detail: { notificationId: banner.id },
        }),
      );

      if (typeof onNavigate === "function") {
        onNavigate(window.location.pathname);
      }
    },
    [onNavigate],
  );

  if (loading && !banners.length) {
    return (
      <Box
        sx={{
          px: 2,
          py: 1,
          display: "flex",
          justifyContent: "center",
          backgroundColor: "#f7fafc",
          borderBottom: "1px solid rgba(0,0,0,0.06)",
        }}
      >
        <CircularProgress size={18} sx={{ color: BRAND_BLUE }} />
      </Box>
    );
  }

  if (!visibleBanners.length) {
    return null;
  }

  return (
    <Box
      sx={{
        position: "sticky",
        top: 64,
        zIndex: (theme) => theme.zIndex.appBar - 1,
        px: { xs: 1, sm: 2 },
        py: 1,
        background: "linear-gradient(180deg, rgba(250,251,248,0.98) 0%, rgba(250,251,248,0.92) 100%)",
        backdropFilter: "blur(8px)",
        borderBottom: "1px solid rgba(0, 51, 108, 0.08)",
      }}
    >
      <Stack spacing={1}>
        {visibleBanners.map((banner) => (
          <Collapse key={banner.id} in timeout={180}>
            <Alert
              severity={severityMap[banner.severity] || "info"}
              icon={false}
              sx={{
                alignItems: "flex-start",
                borderRadius: 3,
                border: `1px solid ${banner.severity === "success" ? `${BRAND_GREEN}44` : `${BRAND_BLUE}22`}`,
                boxShadow: "0 12px 28px rgba(0, 36, 77, 0.08)",
                "& .MuiAlert-message": {
                  width: "100%",
                },
              }}
              action={
                <IconButton
                  aria-label="dismiss banner"
                  color="inherit"
                  size="small"
                  onClick={() => handleClose(banner.id)}
                >
                  <CloseRoundedIcon fontSize="small" />
                </IconButton>
              }
            >
              <ButtonBase
                onClick={() => handleOpenNotification(banner)}
                sx={{
                  width: "100%",
                  display: "block",
                  textAlign: "left",
                  borderRadius: 2,
                  px: 0.25,
                  py: 0.1,
                }}
              >
                <Stack spacing={0.45}>
                  <Typography sx={{ fontWeight: 800, color: BRAND_BLUE }}>
                    You have a new notification
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.primary" }}>
                    Click here to open the full notification and read the details.
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 700,
                      color: banner.severity === "success" ? BRAND_GREEN : BRAND_BLUE,
                    }}
                  >
                    View notification
                  </Typography>
                </Stack>
              </ButtonBase>
            </Alert>
          </Collapse>
        ))}
      </Stack>
    </Box>
  );
}
